import { FC } from 'react';
import clsx from 'clsx';

interface RadioOption {
  value: string;
  label: string;
}

interface RadioGroupProps {
  label?: string;
  name: string;
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  layout?: 'horizontal' | 'vertical';
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const RadioGroup: FC<RadioGroupProps> = ({
  label,
  name,
  options,
  value,
  onChange,
  error,
  layout = 'horizontal',
  required = false,
  disabled = false,
  className,
}) => {
  const handleChange = (optionValue: string) => {
    if (!disabled) {
      onChange(optionValue);
    }
  };

  return (
    <div className={clsx('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        className={clsx(
          'flex gap-4',
          layout === 'horizontal' ? 'flex-row' : 'flex-col'
        )}
        role="radiogroup"
        aria-label={label || name}
      >
        {options.map((option) => {
          const isChecked = value === option.value;
          const radioId = `${name}-${option.value}`;

          return (
            <label
              key={option.value}
              htmlFor={radioId}
              className={clsx(
                'flex items-center gap-3 cursor-pointer select-none',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                id={radioId}
                name={name}
                value={option.value}
                checked={isChecked}
                onChange={() => handleChange(option.value)}
                disabled={disabled}
                className="sr-only"
              />
              <div
                className={clsx(
                  'h-6 w-6 rounded-full border-2 flex items-center justify-center transition-colors shrink-0',
                  isChecked
                    ? 'border-blue-600 dark:border-blue-400'
                    : 'border-gray-400 dark:border-gray-500'
                )}
              >
                {isChecked && (
                  <div className="h-3 w-3 rounded-full bg-blue-600 dark:bg-blue-400" />
                )}
              </div>
              <span className="text-sm text-gray-900 dark:text-gray-100">
                {option.label}
              </span>
            </label>
          );
        })}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};
